import { initializeApp } from 'firebase/app';
import { getDatabase, ref, set, get, update, remove, onValue, off } from 'firebase/database';
import { getCSTDayBounds } from './time';
import { WorkerSession, ShiftSession, TaskSession } from '../types/auth';

const firebaseConfig = {
  apiKey: "AIzaSyB0VL1XLUivVH0TjKrotmLwsgfKhwoz818",
  authDomain: "ninjasgroup-pwa.firebaseapp.com",
  databaseURL: "https://ninjasgroup-pwa-default-rtdb.firebaseio.com",
  projectId: "ninjasgroup-pwa",
  storageBucket: "ninjasgroup-pwa.firebasestorage.app",
  messagingSenderId: "1093446098731",
  appId: "1:1093446098731:web:e1caaedb51038747c36f6e",
  measurementId: "G-ZEQVGX8JNF"
};

const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// Generate a unique device ID
export function generateDeviceId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function subscribeToWorkerSession(workerId: string, callback: (session: WorkerSession | null) => void) {
  const workerRef = ref(database, `workers/${workerId}`);
  
  // Initial check for force logout
  get(workerRef).then((snapshot) => {
    const session = snapshot.exists() ? snapshot.val() : null;
    if (session?.force_logout) {
      callback(session);
    }
  });

  // Subscribe to changes
  const unsubscribe = onValue(workerRef, (snapshot) => {
    const session = snapshot.exists() ? snapshot.val() : null;
    callback(session);
  });

  return () => {
    off(workerRef);
    unsubscribe();
  };
}

export async function checkWorkerSession(workerId: string): Promise<WorkerSession | null> {
  try {
    const snapshot = await get(ref(database, `workers/${workerId}`));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error checking worker session:', error);
    throw error;
  }
}

export async function setWorkerSession(session: WorkerSession): Promise<void> {
  try {
    const existingSession = await checkWorkerSession(session.worker_id);
    
    // If there's an existing session with a different device ID, force logout
    if (existingSession?.is_logged_in && existingSession?.device_id !== session.device_id) {
      await update(ref(database, `workers/${session.worker_id}`), {
        force_logout: true,
        is_logged_in: false,
        last_active: Date.now()
      });

      // Wait a moment to ensure the force logout is processed
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Set the new session
    await set(ref(database, `workers/${session.worker_id}`), {
      ...session,
      last_active: Date.now(),
      force_logout: false,
      is_logged_in: true
    });
  } catch (error) {
    console.error('Error setting worker session:', error);
    throw error;
  }
}

export async function updateWorkerSession(workerId: string, updates: Partial<WorkerSession>): Promise<void> {
  try {
    // Check if session is marked for force logout
    const session = await checkWorkerSession(workerId);
    if (session?.force_logout || !session?.is_logged_in) {
      throw new Error('Session has been logged out');
    }
    
    await update(ref(database, `workers/${workerId}`), {
      ...updates,
      last_active: Date.now(),
    });
  } catch (error) {
    console.error('Error updating worker session:', error);
    throw error;
  }
}

export async function removeWorkerSession(workerId: string): Promise<void> {
  try {
    const shiftSession = await getShiftSession(workerId);
    
    // Only remove the worker session if they're not in an active shift
    // or if they've completed their shift (clocked out)
    if (!shiftSession || shiftSession.status === 'clocked_out') {
      await remove(ref(database, `workers/${workerId}`));
      await remove(ref(database, `tasks/${workerId}`));
      await remove(ref(database, `shifts/${workerId}`));
    } else {
      // If they have an active shift, just mark them as logged out
      await update(ref(database, `workers/${workerId}`), {
        is_logged_in: false,
        force_logout: false,
        last_active: Date.now()
      });
    }
  } catch (error) {
    console.error('Error removing worker session:', error);
    throw error;
  }
}

export async function setTaskSession(session: TaskSession): Promise<void> {
  try {
    await set(ref(database, `tasks/${session.worker_id}/${session.task_id}`), session);
  } catch (error) {
    console.error('Error setting task session:', error);
    throw error;
  }
}

export async function getTaskSession(workerId: string, taskId: number): Promise<TaskSession | null> {
  try {
    const snapshot = await get(ref(database, `tasks/${workerId}/${taskId}`));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error getting task session:', error);
    throw error;
  }
}

export async function updateTaskSession(
  workerId: string,
  taskId: number,
  updates: Partial<TaskSession>
): Promise<void> {
  try {
    await update(ref(database, `tasks/${workerId}/${taskId}`), updates);
  } catch (error) {
    console.error('Error updating task session:', error);
    throw error;
  }
}

export async function removeTaskSession(workerId: string, taskId: number): Promise<void> {
  try {
    await remove(ref(database, `tasks/${workerId}/${taskId}`));
  } catch (error) {
    console.error('Error removing task session:', error);
    throw error;
  }
}

export async function getAllActiveTasks(workerId: string): Promise<TaskSession[]> {
  try {
    const snapshot = await get(ref(database, `tasks/${workerId}`));
    if (!snapshot.exists()) {
      return [];
    }
    
    const tasks = snapshot.val();
    return Object.values(tasks).filter((task: TaskSession) => task.status === 'in_progress');
  } catch (error) {
    console.error('Error getting active tasks:', error);
    throw error;
  }
}

export async function setShiftSession(session: ShiftSession): Promise<void> {
  try {
    const { start, end } = getCSTDayBounds();
    const existingShifts = await get(ref(database, `shifts/${session.worker_id}`));
    
    if (existingShifts.exists()) {
      const shifts = Object.values(existingShifts.val()) as ShiftSession[];
      const hasActiveShift = shifts.some(shift => 
        shift.clockin_time >= start && 
        shift.clockin_time <= end && 
        !shift.clockout_time
      );

      if (hasActiveShift) {
        throw new Error('Worker already has an active shift for today');
      }
    }

    await set(ref(database, `shifts/${session.worker_id}/current`), session);
  } catch (error) {
    console.error('Error setting shift session:', error);
    throw error;
  }
}

export async function getShiftSession(workerId: string): Promise<ShiftSession | null> {
  try {
    const snapshot = await get(ref(database, `shifts/${workerId}/current`));
    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error getting shift session:', error);
    throw error;
  }
}

export async function updateShiftSession(workerId: string, updates: Partial<ShiftSession>): Promise<void> {
  try {
    if (!workerId) {
      throw new Error('Worker ID is required for updating shift session');
    }

    if (Object.keys(updates).length === 0) {
      throw new Error('No updates provided for shift session');
    }

    const currentSession = await getShiftSession(workerId);
    if (!currentSession) {
      throw new Error('No active shift session found');
    }

    // Check if session is marked for force logout
    const workerSession = await checkWorkerSession(workerId);
    if (workerSession?.force_logout || !workerSession?.is_logged_in) {
      throw new Error('Session has been logged out');
    }

    // Validate state transitions
    const newStatus = updates.status;
    if (newStatus === 'clocked_out' && !currentSession.finishjob_time) {
      throw new Error('Must finish job before clocking out');
    }

    if (newStatus === 'job_finished' && !updates.finishjob_time) {
      throw new Error('Finish job time is required');
    }

    // Create merged updates
    const mergedUpdates = {
      ...currentSession,
      ...updates,
      worker_id: workerId
    };

    await update(ref(database, `shifts/${workerId}/current`), mergedUpdates);
  } catch (error) {
    console.error('Error updating shift session:', error);
    throw error;
  }
}

export async function removeShiftSession(workerId: string): Promise<void> {
  try {
    const currentShift = await getShiftSession(workerId);
    
    // Only remove the shift if the worker has clocked out
    if (currentShift?.status === 'clocked_out') {
      await remove(ref(database, `shifts/${workerId}/current`));
    }
  } catch (error) {
    console.error('Error removing shift session:', error);
    throw error;
  }
}

export async function cleanupWorkerSessions(): Promise<void> {
  try {
    const snapshot = await get(ref(database, 'workers'));
    if (!snapshot.exists()) {
      return;
    }

    const sessions = snapshot.val();
    const updates: Record<string, null> = {};
    const now = Date.now();

    for (const [workerId, session] of Object.entries(sessions)) {
      const workerSession = session as WorkerSession;
      if (workerSession.is_logged_in && now - workerSession.last_active > 24 * 60 * 60 * 1000) {
        // Check if worker has an active shift
        const shiftSession = await getShiftSession(workerId);
        
        if (!shiftSession || shiftSession.status === 'clocked_out') {
          // If no active shift or clocked out, remove all data
          updates[`workers/${workerId}`] = null;
          updates[`tasks/${workerId}`] = null;
          updates[`shifts/${workerId}`] = null;
        } else {
          // If active shift exists, just mark as logged out
          await update(ref(database, `workers/${workerId}`), {
            is_logged_in: false,
            force_logout: false,
            last_active: now
          });
        }
      }
    }

    if (Object.keys(updates).length > 0) {
      await update(ref(database), updates);
    }
  } catch (error) {
    console.error('Error cleaning up worker sessions:', error);
    throw error;
  }
}